import { API_TERMS_TEXT, API_TERMS_VERSION } from "@planerius/contracts/public-api"
import type { IntegrationView, PublicApiScope } from "@planerius/contracts/public-api"
import type { ReactNode } from "react"
import { useState } from "react"

import { useCabinetAccess } from "../../access/use-access"
import { createIntegration, fetchIntegrations, narrowAccess, revokeAccess } from "../../api/integrations.api"
import { isApiProblem } from "../../api/problem"
import { JournalDialog } from "../../journal/appointment-dialogs"
import { cx } from "../../lib/cx"
import { resolveBranch } from "../../shell/use-branch"
import { ProblemScreen } from "../../shell/states"
import { Badge, Button, Checkbox, Chip, Chips, Skeleton, TextField } from "../../ui"
import { useResource } from "../directories/use-resource"
import {
  SCOPE_CHOICES,
  branchesLine,
  grantedLine,
  integrationBadge,
  piiLine,
  scopeBadges,
  untilLine,
} from "./developers-model"
import styles from "./developers.module.css"

/**
 * 4-В6 «Кому открыт доступ к данным клиентов» — /developers (макет «Данные клиента и
 * группы - П2», карточка 700).
 *
 * Решения владельца 11.10.2026: раздел открыт по праву «Разработчикам», данные клиентов
 * открывает только владелец со вторым фактором; сейчас — только своя интеграция салона.
 * «Ни одной интеграции — это норма»: экран не подталкивает её заводить.
 *
 * Отзыв — немедленный и подтверждается врезкой под карточкой, а не окном: человек должен
 * видеть, ЧТО именно отзывает, — права и филиалы интеграции остаются на экране.
 */
export function DevelopersScreen(props: {
  readonly onGrant: (id: string) => void
  readonly onWebhooks: (id: string) => void
  readonly onRequests: (id: string) => void
}): ReactNode {
  const { context, permissions } = useCabinetAccess()
  const timeZone = resolveBranch(context, null)?.timezone ?? context.tenant.defaultTimezone
  const [tick, setTick] = useState(0)
  const list = useResource((signal) => fetchIntegrations(signal), `integrations:${String(tick)}`)
  const [creating, setCreating] = useState(false)
  const [narrowing, setNarrowing] = useState<IntegrationView | null>(null)
  const [revoking, setRevoking] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  if (list.problem !== null && list.data === null) {
    return <ProblemScreen problem={list.problem} onRetry={list.reload} />
  }
  const reload = (): void => {
    setTick((value) => value + 1)
  }
  const items = list.data?.items ?? []
  const canWebhooks = permissions.has("webhooks.manage")

  const revoke = async (id: string): Promise<void> => {
    setBusy(true)
    setProblem(null)
    try {
      await revokeAccess(id, {})
      setRevoking(null)
      reload()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(
        error.code === "offline" || error.code === "timeout"
          ? "Доступ не отозван: оборвалась связь. Интеграция всё ещё имеет доступ к вашим данным. Повторите."
          : (error.detail ?? error.title),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.crumbs}>
        <span className={styles.crumbTitle}>Разработчикам</span>
      </div>
      <div className={styles.page}>
        <section className={styles.card} aria-labelledby="developers-title">
          <div className={styles.head}>
            <div className={styles.headText}>
              <h1 id="developers-title" className={styles.title}>
                Кому открыт доступ к данным клиентов
              </h1>
              <div className={styles.sub}>
                Данные клиентов открывает только владелец, со вторым фактором. Заметки о клиентах и фото работ через
                API не отдаются никогда.
              </div>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>
              + Интеграция
            </Button>
          </div>

          {list.data === null ? (
            <Skeleton width="100%" height={140} />
          ) : items.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.strong}>Ни одной интеграции — это норма</div>
              <div className={styles.sub}>
                Доступ нужен, если у салона есть касса, CRM или подрядчик, который читает записи. Тогда владелец
                выпускает ключ с акцептом поручения и вторым фактором.
              </div>
            </div>
          ) : (
            items.map((item) => (
              <IntegrationCard
                key={item.id}
                item={item}
                timeZone={timeZone}
                branches={context.branches}
                canWebhooks={canWebhooks}
                confirming={revoking === item.id}
                busy={busy}
                problem={revoking === item.id ? problem : null}
                onGrant={() => props.onGrant(item.id)}
                onWebhooks={() => props.onWebhooks(item.id)}
                onRequests={() => props.onRequests(item.id)}
                onNarrow={() => setNarrowing(item)}
                onRevoke={() => {
                  setProblem(null)
                  setRevoking(item.id)
                }}
                onKeep={() => setRevoking(null)}
                onConfirmRevoke={() => void revoke(item.id)}
              />
            ))
          )}
        </section>
      </div>

      {creating ? (
        <CreateIntegrationDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            props.onGrant(id)
          }}
        />
      ) : null}
      {narrowing === null ? null : (
        <NarrowDialog
          item={narrowing}
          branches={context.branches}
          onClose={() => setNarrowing(null)}
          onDone={() => {
            setNarrowing(null)
            reload()
          }}
        />
      )}
    </div>
  )
}

function IntegrationCard(props: {
  readonly item: IntegrationView
  readonly timeZone: string
  readonly branches: readonly { readonly id: string; readonly name: string }[]
  readonly canWebhooks: boolean
  readonly confirming: boolean
  readonly busy: boolean
  readonly problem: string | null
  readonly onGrant: () => void
  readonly onWebhooks: () => void
  readonly onRequests: () => void
  readonly onNarrow: () => void
  readonly onRevoke: () => void
  readonly onKeep: () => void
  readonly onConfirmRevoke: () => void
}): ReactNode {
  const { item, timeZone } = props
  const badge = integrationBadge(item, timeZone)
  const pii = piiLine(item)
  const active = item.state === "active"
  const gone = item.state === "revoked" || item.state === "expired"
  const access = item.access

  return (
    <>
      <article
        className={cx(styles.item, item.state === "suspended" && styles.itemAlert, gone && styles.itemGone)}
        aria-label={item.name}
      >
        <div className={styles.itemHead}>
          <div className={styles.headText}>
            <div className={styles.itemName}>{item.name}</div>
            <div className={styles.itemDev}>
              {item.developer.legalName} · ИНН {item.developer.inn}
            </div>
          </div>
          <Badge tone={badge.tone}>{badge.text}</Badge>
        </div>
        {access === null ? null : (
          <>
            <div className={styles.badges}>
              {scopeBadges(access.scopes, gone).map((scope) => (
                <span key={scope.text} className={cx(styles.scope, scope.pii && styles.scopePii)}>
                  {scope.text}
                </span>
              ))}
            </div>
            <div className={styles.facts}>
              <div>
                <span className={styles.factLabel}>Филиалы:</span> {branchesLine(access.branchIds, props.branches)}
              </div>
              <div>
                <span className={styles.factLabel}>Доступ дал:</span> {grantedLine(item, timeZone)}
              </div>
              <div>
                <span className={styles.factLabel}>Выгружено карточек за 7 дней:</span>{" "}
                <b className={styles[pii.tone]}>{pii.text}</b>
              </div>
              <div>
                <span className={styles.factLabel}>Ключ действует до:</span> {untilLine(item, timeZone)}
              </div>
            </div>
          </>
        )}
        <div className={styles.actions}>
          {active ? (
            <>
              <Button size="sm" variant="danger" onClick={props.onRevoke}>
                Отозвать доступ — немедленно
              </Button>
              <Button size="sm" variant="secondary" onClick={props.onNarrow}>
                Сузить права
              </Button>
              {props.canWebhooks ? (
                <Button size="sm" variant="ghost" onClick={props.onWebhooks}>
                  Уведомления о событиях
                </Button>
              ) : null}
            </>
          ) : item.state === "suspended" ? null : (
            <Button size="sm" variant="primary" onClick={props.onGrant}>
              Дать доступ
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={props.onRequests}>
            Журнал обращений
          </Button>
        </div>
      </article>
      {props.confirming ? (
        <div className={styles.confirm} role="alertdialog" aria-label={`Отзыв доступа «${item.name}»`}>
          <div className={styles.strong}>Отозвать доступ интеграции «{item.name}»?</div>
          <div className={styles.confirmText}>
            Интеграция немедленно потеряет доступ к вашим данным. Её ключи перестанут работать, уведомления о событиях
            прекратятся. Если программа сейчас работает в зале, она перестанет видеть записи.
          </div>
          {props.problem === null ? null : (
            <div className={styles.problem} role="alert">
              {props.problem}
            </div>
          )}
          <div className={styles.actionsEnd}>
            <Button size="sm" variant="secondary" onClick={props.onKeep}>
              Оставить
            </Button>
            <Button size="sm" variant="danger" loading={props.busy} onClick={props.onConfirmRevoke}>
              Отозвать
            </Button>
          </div>
        </div>
      ) : null}
    </>
  )
}

/**
 * Завести свою интеграцию: кто подрядчик. Реквизиты — не формальность: без наименования
 * и ИНН обработчика поручение не составить (ч. 3 ст. 6 152-ФЗ). Листа в макете нет
 * (вопрос дизайнеру 69) — поля по контракту, в стиле окон кабинета.
 */
function CreateIntegrationDialog(props: { readonly onClose: () => void; readonly onCreated: (id: string) => void }): ReactNode {
  const [name, setName] = useState("")
  const [legalName, setLegalName] = useState("")
  const [inn, setInn] = useState("")
  const [officer, setOfficer] = useState("")
  const [securityEmail, setSecurityEmail] = useState("")
  const [technicalEmail, setTechnicalEmail] = useState("")
  const [terms, setTerms] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const local =
    name.trim().length < 2
      ? "Назовите интеграцию: «Касса в зале», «CRM подрядчика»."
      : legalName.trim().length < 2 || !/^(\d{10}|\d{12})$/.test(inn.trim())
        ? "Нужны наименование подрядчика и его ИНН (10 или 12 цифр) — они войдут в поручение."
        : officer.trim().length < 2
          ? "Укажите ответственного за персональные данные у подрядчика."
          : !securityEmail.includes("@") || !technicalEmail.includes("@")
            ? "Нужны две почты подрядчика: для инцидентов и техническая."
            : !terms
              ? "Примите условия использования API."
              : null

  const submit = async (): Promise<void> => {
    if (local !== null) {
      setProblem(local)
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      const created = await createIntegration({
        name: name.trim(),
        developer: {
          legalName: legalName.trim(),
          inn: inn.trim(),
          dataProtectionOfficer: officer.trim(),
          securityEmail: securityEmail.trim(),
          technicalEmail: technicalEmail.trim(),
        },
        acceptTerms: true,
        termsVersion: API_TERMS_VERSION,
      })
      props.onCreated(created.id)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  return (
    <JournalDialog title="Своя интеграция салона" onClose={props.onClose}>
      <form
        className={styles.group}
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <TextField label="Название" value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
        <TextField
          label="Подрядчик — наименование"
          hint="Как в договоре: ООО «Эвотор», ИП Смирнов К. В."
          value={legalName}
          maxLength={300}
          onChange={(event) => setLegalName(event.target.value)}
        />
        <TextField label="ИНН подрядчика" inputMode="numeric" value={inn} maxLength={12} onChange={(event) => setInn(event.target.value)} />
        <TextField
          label="Ответственный за персональные данные у подрядчика"
          value={officer}
          maxLength={200}
          onChange={(event) => setOfficer(event.target.value)}
        />
        <TextField
          label="Почта для инцидентов"
          type="email"
          value={securityEmail}
          onChange={(event) => setSecurityEmail(event.target.value)}
        />
        <TextField
          label="Техническая почта"
          hint="Сюда напомним об истечении ключа."
          type="email"
          value={technicalEmail}
          onChange={(event) => setTechnicalEmail(event.target.value)}
        />
        <Checkbox checked={terms} onChange={setTerms}>
          Принимаю условия использования API Planerius (черновик {API_TERMS_VERSION})
        </Checkbox>
        <Button size="sm" variant="ghost" onClick={() => setShowTerms((value) => !value)}>
          {showTerms ? "Скрыть условия" : "Прочитать условия"}
        </Button>
        {showTerms ? <div className={styles.document}>{API_TERMS_TEXT}</div> : null}
        {problem === null ? null : (
          <div className={styles.problem} role="alert">
            {problem}
          </div>
        )}
        <div className={styles.actionsEnd}>
          <Button size="sm" variant="secondary" onClick={props.onClose}>
            Отмена
          </Button>
          <Button size="sm" variant="primary" type="submit" loading={busy}>
            Завести и перейти к доступу
          </Button>
        </div>
      </form>
    </JournalDialog>
  )
}

/**
 * «Сузить права»: только убрать — добавить можно лишь новым доступом с новым поручением.
 * Листа в макете нет (вопросы дизайнеру П2) — галочки текущих прав и филиалы.
 */
function NarrowDialog(props: {
  readonly item: IntegrationView
  readonly branches: readonly { readonly id: string; readonly name: string }[]
  readonly onClose: () => void
  readonly onDone: () => void
}): ReactNode {
  const access = props.item.access
  const current = access?.scopes ?? []
  const [scopes, setScopes] = useState<readonly PublicApiScope[]>(current)
  const allowedBranches =
    access === null || access.branchIds.length === 0 ? props.branches : props.branches.filter((branch) => access.branchIds.includes(branch.id))
  const [branchIds, setBranchIds] = useState<readonly string[]>(access?.branchIds ?? [])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const submit = async (): Promise<void> => {
    if (scopes.length === 0) {
      setProblem("Отметьте хотя бы одно право. Чтобы закрыть всё — отзовите доступ.")
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      await narrowAccess(props.item.id, { scopes: [...scopes], branchIds: [...branchIds] })
      props.onDone()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  return (
    <JournalDialog title={`Сузить права · ${props.item.name}`} onClose={props.onClose}>
      <div className={styles.group}>
        <div className={styles.hint}>Сузить можно сразу; расширить — только новым доступом с новым поручением.</div>
        <div className={styles.scopeList}>
          {SCOPE_CHOICES.filter((choice) => current.includes(choice.scope)).map((choice) => (
            <Checkbox
              key={choice.scope}
              className={styles.scopeRow}
              checked={scopes.includes(choice.scope)}
              onChange={(checked) =>
                setScopes((value) => (checked ? [...value, choice.scope] : value.filter((scope) => scope !== choice.scope)))
              }
            >
              <span className={cx(styles.scopeTitle, choice.pii === true && styles.scopeTitlePii)}>{choice.title}</span>
            </Checkbox>
          ))}
        </div>
        <div className={styles.groupLabel}>Филиалы</div>
        <Chips label="Филиалы">
          {access !== null && access.branchIds.length === 0 ? (
            <Chip active={branchIds.length === 0} onClick={() => setBranchIds([])}>
              Все, включая будущие
            </Chip>
          ) : null}
          {allowedBranches.map((branch) => (
            <Chip
              key={branch.id}
              active={branchIds.includes(branch.id)}
              onClick={() =>
                setBranchIds((value) => (value.includes(branch.id) ? value.filter((id) => id !== branch.id) : [...value, branch.id]))
              }
            >
              Только {branch.name}
            </Chip>
          ))}
        </Chips>
        {problem === null ? null : (
          <div className={styles.problem} role="alert">
            {problem}
          </div>
        )}
        <div className={styles.actionsEnd}>
          <Button size="sm" variant="secondary" onClick={props.onClose}>
            Отмена
          </Button>
          <Button size="sm" variant="primary" loading={busy} onClick={() => void submit()}>
            Сузить
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}
